/**
 * UpcomingEvents — "¿qué tengo agendado?" next 3 calendar events.
 *
 * Tile chico del Dashboard con los próximos eventos del char (FC ops,
 * mining fleets, industry help, ratting, etc). El industrialist mira el
 * dashboard y ve "hoy a las 22:00 tengo fleet de mining op" sin abrir
 * el cliente de EVE.
 *
 * Data flow:
 *   - useQuery ['calendar-upcoming', limit] → /api/calendar/upcoming?limit=N
 *   - staleTime 5min — los eventos no cambian tan seguido y ESI ETag
 *     cachea por character de todas formas.
 *   - refetchOnWindowFocus para "volví al tab, refrescame".
 *
 * UX:
 *   - Top N events sorted by event_date ASC.
 *   - Cada row: title + owner (name + type) + time + duration + response
 *     badge (accepted/declined/tentative).
 *   - "importance: true" → badge rojo junto al title (FC marcó como
 *     importante).
 *   - Empty state: "Sin eventos próximos."
 *   - Scope missing (note: 'scope_missing'): banner de re-auth con link
 *     a logout/login.
 *
 * Time format:
 *   - event_date es ISO datetime UTC. Mostramos HH:MM UTC + día relativo
 *     ("hoy" / "mañana" / "DD MMM"). Para eventos >7 días: solo fecha.
 */

import { useQuery } from '@tanstack/react-query'
import { useAuth } from '../hooks/useAuth'
import { api } from '../api'
import { Link } from 'react-router-dom'

interface UpcomingEvent {
  event_id: number
  event_date: string
  title: string
  duration: number
  response: 'accepted' | 'declined' | 'tentative' | 'undecided'
  importance: boolean
  owner_id: number
  owner_name: string
  owner_type: 'character' | 'corporation' | 'alliance' | 'faction'
}

const DEFAULT_LIMIT = 3

export function UpcomingEvents({ limit = DEFAULT_LIMIT }: { limit?: number }) {
  const { data: me } = useAuth()
  const { data, isLoading, isError, error, dataUpdatedAt } = useQuery({
    queryKey: ['calendar-upcoming', limit],
    queryFn: () => api.calendarUpcoming(limit),
    staleTime: 5 * 60_000,
    refetchOnWindowFocus: true,
  })

  // 1) No hay char autenticado → no muestro nada (no aplica).
  if (!me?.character) return null

  // 2) Scope missing — banner explícito + link a re-auth.
  if (data?.note === 'scope_missing') {
    return (
      <div className="bg-eve-warn/10 border border-eve-warn/30 rounded-md p-4 text-eve-warn text-sm flex items-center justify-between gap-3 flex-wrap">
        <span>
          El calendario requiere el scope{' '}
          <code className="bg-eve-bg px-1 rounded text-xs">
            esi-calendar.read_calendar_events.v1
          </code>
          . Re-autenticá para desbloquearlo.
        </span>
        <Link
          to="/login"
          className="text-eve-accent hover:underline text-xs uppercase tracking-wider font-semibold"
        >
          re-autenticar →
        </Link>
      </div>
    )
  }

  if (isError) {
    return (
      <div className="bg-eve-danger/10 border border-eve-danger/30 rounded-md p-3 text-eve-danger text-sm">
        Error cargando calendario: {(error as Error).message}
      </div>
    )
  }

  if (isLoading || !data) {
    return (
      <div className="bg-eve-panel border border-eve-border rounded-md p-4 space-y-2">
        <div className="h-3 w-1/3 bg-eve-border/40 rounded animate-pulse" />
        <div className="h-4 bg-eve-border/30 rounded animate-pulse" />
        <div className="h-4 bg-eve-border/30 rounded animate-pulse w-5/6" />
      </div>
    )
  }

  const events = data.events

  return (
    <div className="bg-gradient-to-br from-eve-panel to-eve-bg border border-eve-border rounded-md p-4">
      <div className="flex items-baseline justify-between flex-wrap gap-2">
        <div>
          <div className="text-eve-muted text-[10px] uppercase tracking-widest font-medium">
            Próximos eventos
          </div>
          <div className="font-display text-lg font-semibold text-eve-text mt-0.5">
            {events.length === 0
              ? 'Sin eventos'
              : `${events.length} ${events.length === 1 ? 'agendado' : 'agendados'}`}
          </div>
        </div>
        <div className="text-[10px] text-eve-muted-2 font-mono italic">
          ref {dataUpdatedAt ? new Date(dataUpdatedAt).toLocaleTimeString() : '—'} UTC
        </div>
      </div>

      {events.length === 0 ? (
        <div className="mt-3 text-eve-muted text-sm italic">
          Calendario vacío por ahora.
        </div>
      ) : (
        <ul className="mt-3 space-y-2">
          {events.map((e) => (
            <EventRow key={e.event_id} event={e} />
          ))}
        </ul>
      )}
    </div>
  )
}

function EventRow({ event }: { event: UpcomingEvent }) {
  const { when, fullDate } = formatWhen(event.event_date, event.duration)
  return (
    <li className="border-l-2 border-eve-border/60 pl-3 py-1 hover:border-eve-accent/40 transition-colors">
      <div className="flex items-baseline gap-2 flex-wrap">
        {event.importance && (
          <span
            className="inline-block bg-eve-danger/15 border border-eve-danger/40 text-eve-danger text-[9px] uppercase tracking-wider font-semibold rounded px-1.5 py-0.5"
            title="Marcado como importante por el organizador"
          >
            !
          </span>
        )}
        <span className="text-sm font-medium text-eve-text truncate">
          {event.title}
        </span>
        <ResponseBadge response={event.response} />
      </div>
      <div className="text-[11px] text-eve-muted mt-0.5 flex items-center gap-2 flex-wrap">
        <span className="font-mono text-eve-text">{when}</span>
        <span className="text-eve-muted-2">·</span>
        <span>
          {event.duration} {event.duration === 1 ? 'min' : 'min'}
        </span>
        <span className="text-eve-muted-2">·</span>
        <span className="italic">
          por {event.owner_name}
          <span className="text-eve-muted-2"> ({ownerTypeLabel(event.owner_type)})</span>
        </span>
      </div>
      <div className="text-[10px] text-eve-muted-2 mt-0.5 font-mono italic">
        {fullDate}
      </div>
    </li>
  )
}

function ResponseBadge({
  response,
}: {
  response: UpcomingEvent['response']
}) {
  const map: Record<UpcomingEvent['response'], { label: string; tone: string }> = {
    accepted: {
      label: 'aceptado',
      tone: 'bg-eve-success/15 border-eve-success/40 text-eve-success',
    },
    declined: {
      label: 'rechazado',
      tone: 'bg-eve-danger/15 border-eve-danger/40 text-eve-danger',
    },
    tentative: {
      label: 'quizás',
      tone: 'bg-eve-warn/15 border-eve-warn/40 text-eve-warn',
    },
    undecided: {
      label: 'pendiente',
      tone: 'bg-eve-muted/15 border-eve-muted/40 text-eve-muted',
    },
  }
  const v = map[response]
  return (
    <span
      className={`inline-block text-[9px] uppercase tracking-wider font-semibold rounded px-1.5 py-0.5 border ${v.tone}`}
    >
      {v.label}
    </span>
  )
}

function ownerTypeLabel(t: UpcomingEvent['owner_type']): string {
  switch (t) {
    case 'character':
      return 'piloto'
    case 'corporation':
      return 'corp'
    case 'alliance':
      return 'alianza'
    case 'faction':
      return 'facción'
    default:
      return t
  }
}

/**
 * formatWhen: relative day label ("hoy" / "mañana" / "Lun 16 Sep") + HH:MM UTC.
 * Devuelve `when` (display principal) y `fullDate` (ISO UTC completo, italic).
 */
function formatWhen(iso: string, durationMin: number): { when: string; fullDate: string } {
  const eventDate = new Date(iso)
  const now = new Date()
  // Diff en días calendario UTC.
  const eventDay = Date.UTC(
    eventDate.getUTCFullYear(),
    eventDate.getUTCMonth(),
    eventDate.getUTCDate(),
  )
  const todayDay = Date.UTC(
    now.getUTCFullYear(),
    now.getUTCMonth(),
    now.getUTCDate(),
  )
  const dayDiff = Math.round((eventDay - todayDay) / 86_400_000)
  const time = `${pad(eventDate.getUTCHours())}:${pad(eventDate.getUTCMinutes())}`

  let when: string
  if (dayDiff === 0) when = `hoy ${time} UTC`
  else if (dayDiff === 1) when = `mañana ${time} UTC`
  else if (dayDiff > 1 && dayDiff < 7) {
    when = `${weekdayShort(eventDate.getUTCDay())} ${time} UTC`
  } else {
    when = `${pad(eventDate.getUTCDate())} ${monthShort(eventDate.getUTCMonth())} ${time} UTC`
  }

  const endDate = new Date(eventDate.getTime() + durationMin * 60_000)
  const fullDate = `${iso} → ${pad(endDate.getUTCHours())}:${pad(endDate.getUTCMinutes())} UTC`

  return { when, fullDate }
}

function pad(n: number): string {
  return n < 10 ? `0${n}` : String(n)
}

function weekdayShort(d: number): string {
  return ['Dom', 'Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb'][d] ?? '?'
}

function monthShort(m: number): string {
  return (
    ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'][
      m
    ] ?? '?'
  )
}
